import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Grid,
  Typography,
  Box,
  Card,
  CardContent,
  useTheme,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import {
  FiCreditCard,
  FiUsers,
  FiTarget,
  FiBell,
  FiMail,
  FiMessageCircle,
} from "react-icons/fi";
import {
  donationsAPI,
  contactsAPI,
  campaignsAPI,
  remindersAPI,
} from "../services/api";
import { colors } from "../theme/modernTheme";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MODULE_PERMISSIONS } from "../utils/modulePermissions";

const StatCard = ({ title, value, icon, color, to, onClick }) => {
  const content = (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: `1px solid ${colors.border.main}`,
        borderRadius: "4px",
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
        "&:hover": {
          borderColor: color,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        },
      }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: "4px",
              bgcolor: `${color}15`, // 15% opacity
              color: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            {icon}
          </Box>
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: colors.text.primary, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, fontWeight: 500 }}>
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  if (onClick) {
    return (
      <Box onClick={onClick} style={{ textDecoration: "none" }}>
        {content}
      </Box>
    );
  }

  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      {content}
    </Link>
  );
};

const StatCardWithMetrics = ({
  title,
  value,
  icon,
  color,
  to,
  metrics,
  onClick,
  onMetricClick,
  infoText,
}) => {
  const content = (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: `1px solid ${colors.border.main}`,
        borderRadius: "4px",
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
        "&:hover": {
          borderColor: color,
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        },
      }}>
      <CardContent sx={{ p: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: "4px",
              bgcolor: `${color}15`,
              color: color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            {icon}
          </Box>
          {infoText && (
            <Tooltip
              title={infoText}
              arrow
              placement="top"
              sx={{
                "& .MuiTooltip-tooltip": {
                  fontSize: "0.875rem",
                  maxWidth: 300,
                },
              }}>
              <IconButton
                size="small"
                sx={{
                  color: colors.text.secondary,
                  "&:hover": {
                    color: colors.primary.main,
                    backgroundColor: "transparent",
                  },
                }}
                onClick={(e) => e.stopPropagation()}>
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, color: colors.text.primary, mb: 0.5 }}>
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, fontWeight: 500, mb: 2 }}>
          {title}
        </Typography>
        {metrics && (
          <Box
            display="flex"
            gap={2}
            sx={{
              pt: 2,
              borderTop: `1px solid ${colors.border.main}`,
            }}>
            {metrics.map((metric, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  cursor: metric.onClick ? "pointer" : "inherit",
                  "&:hover": metric.onClick
                    ? {
                        "& .MuiTypography-h6": {
                          color: colors.primary.main,
                        },
                      }
                    : {},
                }}
                onClick={(e) => {
                  if (metric.onClick) {
                    e.stopPropagation();
                    metric.onClick();
                  }
                }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: colors.text.primary,
                    fontSize: "1.25rem",
                    transition: "color 0.2s",
                  }}>
                  {metric.value}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text.secondary,
                    fontSize: "0.75rem",
                  }}>
                  {metric.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (onClick) {
    return (
      <Box onClick={onClick} style={{ textDecoration: "none" }}>
        {content}
      </Box>
    );
  }

  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      {content}
    </Link>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { hasModulePermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    donations: { count: 0, total: 0 },
    contacts: 0,
    campaigns: 0,
    reminders: {
      totalActive: 0,
      activeRecurringReminders: 0,
      totalPledges: 0,
    },
    email: {
      totalSent: 0,
      totalOpened: 0,
      totalBounced: 0,
    },
    whatsapp: {
      totalDelivered: 0,
      totalRead: 0,
      totalFailed: 0,
    },
  });

  // Check permissions for each module
  const canViewDonations = hasModulePermission(MODULE_PERMISSIONS.DONATION);
  const canViewContacts = hasModulePermission(MODULE_PERMISSIONS.CONTACTS);
  const canViewCampaigns =
    hasModulePermission(MODULE_PERMISSIONS.EMAIL_MARKETING) ||
    hasModulePermission(MODULE_PERMISSIONS.WHATSAPP_MARKETING);
  const canViewEmailMarketing = hasModulePermission(
    MODULE_PERMISSIONS.EMAIL_MARKETING
  );
  const canViewWhatsAppMarketing = hasModulePermission(
    MODULE_PERMISSIONS.WHATSAPP_MARKETING
  );

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);

      // Build array of API calls based on permissions
      const apiCalls = [];
      const apiCallMap = {};

      if (canViewDonations) {
        apiCallMap.donations = apiCalls.length;
        apiCalls.push(donationsAPI.getStatistics());
      }

      if (canViewContacts) {
        apiCallMap.contacts = apiCalls.length;
        apiCalls.push(contactsAPI.getAll({ limit: 0 }));
      }

      if (canViewCampaigns) {
        apiCallMap.campaigns = apiCalls.length;
        apiCalls.push(campaignsAPI.getAll({ limit: 0 }));
      }

      if (canViewDonations) {
        apiCallMap.reminders = apiCalls.length;
        apiCalls.push(remindersAPI.getStatistics());
      }

      if (canViewEmailMarketing) {
        apiCallMap.email = apiCalls.length;
        apiCalls.push(campaignsAPI.getEmailStatistics());
      }

      if (canViewWhatsAppMarketing) {
        apiCallMap.whatsapp = apiCalls.length;
        apiCalls.push(campaignsAPI.getWhatsAppStatistics());
      }

      // Execute only the permitted API calls
      const results = await Promise.all(apiCalls);

      // Map results back to stats
      const newStats = { ...stats };

      if (apiCallMap.donations !== undefined) {
        newStats.donations = results[apiCallMap.donations].data.total;
      }

      if (apiCallMap.contacts !== undefined) {
        newStats.contacts = results[apiCallMap.contacts].pagination.total;
      }

      if (apiCallMap.campaigns !== undefined) {
        newStats.campaigns = results[apiCallMap.campaigns].pagination.total;
      }

      if (apiCallMap.reminders !== undefined) {
        newStats.reminders = results[apiCallMap.reminders].data;
      }

      if (apiCallMap.email !== undefined) {
        newStats.email = results[apiCallMap.email].data;
      }

      if (apiCallMap.whatsapp !== undefined) {
        newStats.whatsapp = results[apiCallMap.whatsapp].data;
      }

      setStats(newStats);
    } catch (error) {
      // Silently handle errors
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box mb={4}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: colors.text.primary,
            mb: 1,
          }}>
          Dashboard Overview
        </Typography>
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px">
          <CircularProgress size={48} sx={{ color: colors.primary.main }} />
        </Box>
      ) : (
        <>
          <Grid container spacing={3} mb={3}>
            {canViewDonations && (
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Total Donations"
                  value={`₹ ${stats.donations.amount?.toLocaleString() || 0}`}
                  icon={<FiCreditCard size={24} />}
                  color={colors.primary.main}
                  to="/donations"
                />
              </Grid>
            )}

            {canViewContacts && (
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Total Contacts"
                  value={stats.contacts}
                  icon={<FiUsers size={24} />}
                  color={colors.primary.main}
                  to="/contacts"
                />
              </Grid>
            )}

            {canViewCampaigns && (
              <Grid item xs={12} sm={6} md={4}>
                <StatCard
                  title="Total Campaigns"
                  value={stats.campaigns}
                  icon={<FiTarget size={24} />}
                  color={colors.primary.main}
                  to="/campaigns"
                />
              </Grid>
            )}
          </Grid>

          <Grid container spacing={3} mb={4}>
            {canViewDonations && (
              <Grid item xs={12} sm={6} md={4}>
                <StatCardWithMetrics
                  title="Active Reminders & Pledges"
                  value={stats.reminders.totalActive}
                  icon={<FiBell size={24} />}
                  color={colors.primary.main}
                  infoText="Only Active Reminders and Pledges Donation are counted."
                  onClick={() => navigate("/reminders")}
                  metrics={[
                    {
                      label: "Active Recurring",
                      value: stats.reminders.activeRecurringReminders,
                      onClick: () =>
                        navigate("/reminders?type=RECURRING&status=ACTIVE"),
                    },
                    {
                      label: "Total Pledges",
                      value: stats.reminders.totalPledges,
                      onClick: () => navigate("/donations?type=pledge"),
                    },
                  ]}
                />
              </Grid>
            )}

            {canViewEmailMarketing && (
              <Grid item xs={12} sm={6} md={4}>
                <StatCardWithMetrics
                  title="Email Communications"
                  value={stats.email.emailCommunications || 0}
                  icon={<FiMail size={24} />}
                  color={colors.primary.main}
                  onClick={() => navigate("/campaigns?channel=email")}
                  infoText="Only Active and Completed Email Campaigns are counted. Draft Campaigns are not included."
                  metrics={[
                    {
                      label: "Emails Opened",
                      value: stats.email.totalOpened,
                    },
                    {
                      label: "Emails Bounced",
                      value: stats.email.totalBounced,
                    },
                  ]}
                />
              </Grid>
            )}

            {canViewWhatsAppMarketing && (
              <Grid item xs={12} sm={6} md={4}>
                <StatCardWithMetrics
                  title="WhatsApp Communications"
                  value={stats.whatsapp.whatsappCommunications || 0}
                  icon={<FiMessageCircle size={24} />}
                  color={colors.primary.main}
                  onClick={() => navigate("/campaigns?channel=whatsapp")}
                  infoText="Only Active and Completed WhatsApp Campaigns are counted. Draft Campaigns are not included."
                  metrics={[
                    {
                      label: "Messages Read",
                      value: stats.whatsapp.totalRead,
                    },
                    {
                      label: "Messages Failed",
                      value: stats.whatsapp.totalFailed,
                    },
                  ]}
                />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </Box>
  );
}
